/**
 * Підписання через ОФІЦІЙНИЙ web-віджет ІІТ (рішення власника 2026-08-14:
 * «хмарні підписи»): iframe з eu.iit.com.ua дає ВСІ носії — файловий
 * ключ, апаратний токен і ХМАРНІ сервіси (SmartID ПриватБанку, хмарні
 * сховища КНЕДП тощо) — без окремих договорів з провайдерами.
 *
 * Ключ обробляється НА СТОРОНІ віджета (iframe ІІТ) — до нас приходить
 * лише ГОТОВИЙ підпис; наш verify-сервіс так само криптографічно його
 * перевіряє (дані підписанта беремо з сертифіката на сервері).
 *
 * Формат: CAdES_X_Long (мітка часу + OCSP-дані ВБУДОВАНІ у підпис —
 * найкращий для довгострокового зберігання); якщо провайдер/АЦСК не
 * дає X-Long — фолбек CAdES_T → CAdES_BES.
 *
 * Лоадер eusign.js вендорено у /eusign/eusw.js (27КБ, офіційний скрипт
 * інтеграції); сам віджет завантажується з eu.iit.com.ua.
 */
import { useEffect, useRef, useState } from 'react';

const HOSTED_WIDGET_URI = 'https://eu.iit.com.ua/sign-widget/v20240301/';
const LOADER_SRC = '/eusign/eusw.js';

// Self-hosted віджет (2026-08-14, рішення власника): якщо на НАШОМУ
// домені розгорнуто /sign-widget/ (дистрибутив ІІТ, див.
// sign-widget/README.md) — використовуємо ЙОГО (whitelist доменів під
// нашим контролем); інакше — хостований ІІТ (потребує реєстрації
// домену у ІІТ). Перевірка один раз на сесію.
let widgetUriPromise: Promise<string> | null = null;
function resolveWidgetUri(): Promise<string> {
  if (widgetUriPromise) return widgetUriPromise;
  widgetUriPromise = fetch('/sign-widget/', { method: 'HEAD' })
    .then((r) => (r.ok ? `${window.location.origin}/sign-widget/` : HOSTED_WIDGET_URI))
    .catch(() => HOSTED_WIDGET_URI);
  return widgetUriPromise;
}

// Мінімальний типаж офіційного лоадера (eusw.js / eusign-idgov.js).
interface EndUserInstance {
  ReadPrivateKey(): Promise<unknown[]>;
  AddEventListener?(eventType: number, listener: (e: { url?: string; qrCode?: string }) => void): Promise<unknown>;
  SignData(
    data: Uint8Array, external?: boolean, asBase64String?: boolean,
    signAlgo?: number, previousSign?: null, signType?: number,
  ): Promise<string>;
  destroy(): void;
}
interface EndUserCtor {
  new (parentId: string, id: string, uri: string, formType: number): EndUserInstance;
  FormType: { ReadPKey: number };
  SignType: { CAdES_BES: number; CAdES_T: number; CAdES_X_Long: number };
  EventType?: { ConfirmKSPOperation: number };
}
declare global {
  interface Window { EndUser?: EndUserCtor }
}

let loaderPromise: Promise<void> | null = null;
let loadedSrc: string | null = null;
function loadWidgetScript(src: string = LOADER_SRC): Promise<void> {
  if (window.EndUser && loadedSrc === src) return Promise.resolve();
  if (loaderPromise && loadedSrc === src) return loaderPromise;
  loadedSrc = src;
  loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => { loaderPromise = null; reject(new Error('Не вдалося завантажити віджет підпису')); };
    document.head.appendChild(s);
  });
  return loaderPromise;
}

export default function WidgetSign({ getData, onSigned, busyLabel, widgetUri, loaderSrc, providerLabel }: {
  /** Дані для підпису (тягнуться ЛИШЕ при натисканні «підписати»). */
  getData: () => Promise<Uint8Array>;
  /** Готовий підпис (base64 CAdES) — відправка на сервер. */
  onSigned: (signatureB64: string) => Promise<void>;
  busyLabel?: string;
  /** ч.741: явний URI iframe (ІСЕІ id.gov.ua test/prod) — без автовизначення. */
  widgetUri?: string | null;
  /** Лоадер, що відповідає версії віджета (id.gov.ua → /eusign/eusign-idgov.js). */
  loaderSrc?: string | null;
  providerLabel?: string | null;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const euRef = useRef<EndUserInstance | null>(null);
  const idRef = useRef(`eusw-${Math.random().toString(36).slice(2, 9)}`);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Дія.Підпис / хмарні ключі: підтвердження операції QR-кодом у застосунку.
  const [qr, setQr] = useState<{ url?: string; qrCode?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [, uri] = await Promise.all([
          loadWidgetScript(loaderSrc || LOADER_SRC),
          widgetUri ? Promise.resolve(widgetUri) : resolveWidgetUri(),
        ]);
        if (cancelled || !hostRef.current || !window.EndUser) return;
        euRef.current = new window.EndUser(
          idRef.current, `${idRef.current}-frame`, uri,
          window.EndUser.FormType.ReadPKey);
        setReady(true);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      try { euRef.current?.destroy(); } catch { /* no-op */ }
      euRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetUri, loaderSrc]);

  const doSign = async () => {
    const eu = euRef.current;
    const EU = window.EndUser;
    if (!eu || !EU) return;
    setBusy(true);
    setError(null);
    try {
      await eu.ReadPrivateKey();   // віджет сам веде юзера (файл/токен/хмара)
      // Підтвердження операції QR-кодом (Дія.Підпис / хмарний ключ).
      if (eu.AddEventListener && EU.EventType?.ConfirmKSPOperation !== undefined) {
        try { await eu.AddEventListener(EU.EventType.ConfirmKSPOperation, (ev) => setQr(ev)); }
        catch { /* старіший віджет без подій — ок */ }
      }
      const data = await getData();
      // X-Long → T → BES (залежить від можливостей АЦСК/провайдера).
      let sig: string | null = null;
      for (const st of [EU.SignType.CAdES_X_Long, EU.SignType.CAdES_T,
                        EU.SignType.CAdES_BES]) {
        try {
          sig = await eu.SignData(data, false, true, undefined, null, st);
          break;
        } catch (e) {
          if (st === EU.SignType.CAdES_BES) throw e;
        }
      }
      setQr(null);
      await onSigned(sig!);
    } catch (e) {
      setQr(null);
      const d = (e as { message?: string })?.message;
      setError(d || 'Не вдалося підписати');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Батьківський елемент iframe віджета. ч.1442: висота явна — iframe тягнеться на 100%
          висоти батька, а з самою мінімальною висотою лишався 150 px і обрізав вікно id.gov.ua. */}
      <div id={idRef.current} ref={hostRef} className="eusw-host" />
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
        marginTop: '0.6rem' }}>
        <button className="primary" disabled={!ready || busy}
          onClick={() => void doSign()}>
          {busy ? (busyLabel ?? 'підписання…') : 'підписати'}
        </button>
        <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
          оберіть носій у вікні вище ({providerLabel ?? 'файл / токен / хмарний'}), далі —
          «підписати»
        </span>
      </div>
      {qr?.qrCode && (
        <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: 'var(--bg-panel)',
          border: '1px solid var(--border)', borderRadius: 8, display: 'inline-block' }}>
          <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>
            Підтвердіть операцію у застосунку: відскануйте QR-код
          </div>
          <a href={qr.url ?? '#'}>
            <img src={`data:image/bmp;base64,${qr.qrCode}`} alt="QR" style={{ padding: 6, background: 'var(--bg-panel)' }} />
          </a>
        </div>
      )}
      <div style={{ color: 'var(--muted)', fontSize: '0.75rem', marginTop: '0.35rem' }}>
        Якщо у вікні «Доступ заборонено» — домен сайту ще не зареєстровано
        {widgetUri ? ' в ІСЕІ (id.gov.ua) для цього середовища' : ' у ІІТ для web-віджета'}
        {' '}(тимчасово скористайтесь файловим ключем).
      </div>
      {error && <p role="alert" style={{ color: 'var(--badge-red-fg)', marginTop: '0.4rem' }}>{error}</p>}
    </div>
  );
}
